import {
  Actions,
  ButtonLink,
  Divider,
  Heading,
  IconNewWindow,
  PageBlock,
  Stack,
  Text,
  Tiles,
} from 'braid-design-system';
import { SmartTextLink } from 'scoobie';

import { ContentHeader } from 'src/components/ContentHeader';
import { FlexPageWithFooter } from 'src/components/FlexPageWithFooter';
import { PageIcon } from 'src/components/PageIcon';
import { TileLink } from 'src/components/TileLink';

const Foreword = () => (
  <Stack space="gutter">
    <Text>
      Welcome to our developer site, home of technical documentation for the{' '}
      <SmartTextLink href="https://www.seek.com.au/about/">
        SEEK Group
      </SmartTextLink>
      . Approved providers can integrate their recruitment software with the
      SEEK API to access our employment marketplace across the Asia Pacific.
    </Text>

    <Text>
      Visit the{' '}
      <SmartTextLink href="https://talent.seek.com.au/partners/partner-with-seek">
        Partner Hub
      </SmartTextLink>{' '}
      for a high-level overview of our product suite and commercial
      partnerships.
    </Text>
  </Stack>
);

const GettingStarted = () => (
  <Stack space="gutter">
    <Heading level="4">New to the SEEK API?</Heading>

    <Text>
      Start at the top of our documentation and work your way through:
    </Text>

    <Actions>
      <ButtonLink
        href="/introduction"
        icon={<PageIcon icon="Introduction" />}
        variant="soft"
      >
        Getting started
      </ButtonLink>
    </Actions>

    <Text>
      Our introductory content outlines the SEEK API and our integration
      process, then details each technology component in order:
    </Text>

    <Tiles columns={[1, 1, 2]} space="small">
      <TileLink
        description="Securely connect to SEEK with OAuth 2.0 flows"
        href="/auth"
        icon={<PageIcon icon="Auth" />}
        title="Auth"
      />

      <TileLink
        description="Access our API endpoint and follow best practices"
        href="/graphql"
        icon={<PageIcon icon="GraphQL" />}
        title="GraphQL"
      />

      <TileLink
        description="Embed pre-built components to reduce integration effort"
        href="/introduction/web-components"
        icon={<PageIcon icon="Panel" />}
        title="Web components"
      />

      <TileLink
        description="Be notified when an action occurs on our marketplace"
        href="/events"
        icon={<PageIcon icon="Webhook" />}
        title="Events"
      />
    </Tiles>

    <Text>
      From there, you can progress to build or manage your integration.
    </Text>
  </Stack>
);

const UseCases = () => (
  <Stack space="gutter">
    <Heading level="4">Building a new product or feature</Heading>

    <Text>
      Jump to the relevant use case and follow the integration tutorial:
    </Text>

    <Tiles columns={[1, 1, 3]} space="small">
      <TileLink
        description="Advertise positions on SEEK with custom questions, granular
              locations, and variable pricing"
        href="/use-cases/job-posting"
        icon={<PageIcon icon="JobPosting" />}
        title="Job Posting"
      />

      <TileLink
        description="Export candidate applications from SEEK into your recruitment software"
        href="/use-cases/application-export"
        icon={<PageIcon icon="OptimisedApply" />}
        title="Optimised Apply"
      />

      <TileLink
        description="Link candidate profiles between your software and SEEK Talent Search (coming soon)"
        href="/use-cases/proactive-sourcing"
        icon={<PageIcon icon="TalentSearchConnect" />}
        title="Talent Search Connect"
      />
    </Tiles>
  </Stack>
);

const Guides = () => (
  <Stack space="gutter">
    <Heading level="4">Managing your integration</Heading>

    <Text component="h5" weight="medium">
      Release cadence
    </Text>

    <Stack space="medium">
      <Text>
        Features are incrementally added to and deprecated from the SEEK API as
        we continuously evolve our products. It’s important to evaluate how
        these changes affect your integration with SEEK.
      </Text>

      <Text>
        Subscribe to our release notes to stay informed of changes as they reach
        general availability and evolve your integration accordingly:
      </Text>
    </Stack>

    <Actions>
      <ButtonLink
        href="/introduction/release-notes"
        icon={<PageIcon icon="ReleaseNotes" />}
        variant="soft"
      >
        Release notes
      </ButtonLink>
    </Actions>

    <Text component="h5" weight="medium">
      Expectations
    </Text>

    <Text>
      Recruitment software providers are expected to deliver the latest and most
      valuable experiences to our mutual hirers within a reasonable period from
      when they are made generally available.
    </Text>

    <Text>
      SEEK will revise the baseline requirements for a marketplace integration
      on a scheduled basis, and supply a guide to plan and integrate changes
      that you have yet to include since the last revision:
    </Text>

    <Actions>
      <ButtonLink
        href="/guides"
        icon={<PageIcon icon="GuideIndex" />}
        variant="soft"
      >
        How-to guides
      </ButtonLink>
    </Actions>

    <Text>
      You should also familiarise yourself with our suite of developer tools:
    </Text>

    <Tiles columns={[1, 1, 3]} space="small">
      <TileLink
        description={
          <>
            Log in to configure and manage your integration in an admin UI{' '}
            <IconNewWindow />
          </>
        }
        href="https://manage.developer.seek.com"
        icon={<PageIcon icon="DeveloperDashboard" />}
        title="Dashboard"
      />

      <TileLink
        description={
          <>
            Inspect low-level details of our GraphQL operations & types{' '}
            <IconNewWindow />
          </>
        }
        href="https://developer.seek.com/schema"
        icon={<PageIcon icon="Schema" />}
        title="Schema"
      />

      <TileLink
        description={
          <>
            Check for incidents and upcoming maintenance <IconNewWindow />
          </>
        }
        href="https://status.seek.com"
        icon={<PageIcon icon="StatusPage" />}
        title="Status page"
      />
    </Tiles>
  </Stack>
);

export const HomePage = () => (
  <PageBlock width="large">
    <ContentHeader>SEEK API</ContentHeader>

    <Divider />

    <FlexPageWithFooter>
      <Stack dividers space="xlarge">
        <Foreword />

        <GettingStarted />

        <UseCases />

        <Guides />
      </Stack>
    </FlexPageWithFooter>
  </PageBlock>
);
